import { useState, useEffect } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { LanguageToggle } from "../components/LanguageToggle";
import { Button } from "../components/ui/button";
import {
	SectionHeader,
	useLandingDocumentScroll,
	useMetricoolTracker,
	useRevealOnScroll,
} from "../components/landing/landingShared";
import {
	ArrowRight,
	Bell,
	Bot,
	CalendarDays,
	Check,
	ChevronDown,
	ChevronRight,
	Dumbbell,
	HeartPulse,
	Layers,
	Menu,
	MessageSquare,
	Percent,
	Plug,
	Receipt,
	RotateCcw,
	Smartphone,
	Sparkles,
	Timer,
	Trophy,
	Users,
	X,
} from "lucide-react";

/**
 * Landing pubblica di Kraveon (rotta "/").
 * Target primario: coach, preparatori e box. Sezione dedicata a Box/Team.
 * Stile: dark "tactical", volt green, Barlow Condensed per i titoli.
 * Scelte editoriali (ott 2026): niente prezzi, niente numeri della beta,
 * manifesto "da professionisti" invariato in attesa dei contenuti "chi siamo".
 * Le anteprime prodotto sono componenti illustrativi, non screenshot.
 */

const COPY = {
	it: {
		nav: [
			{ href: "#prodotto", label: "Prodotto" },
			{ href: "#ai", label: "AI" },
			{ href: "#atleti", label: "Atleti" },
			{ href: "#box", label: "Box & Team" },
			{ href: "#faq", label: "FAQ" },
		],
		login: "Accedi",
		app: "Vai all'app",
		menu: "Menu",
		hero: {
			kicker: "Piattaforma di coaching per coach, preparatori e box",
			h1a: "Meno tempo a gestire.",
			h1b: "Più tempo per ",
			h1accent: "allenare",
			sub: "Programmi, diario atleta, feedback, gare e incassi in un'unica piattaforma. Con un assistente AI che lavora dentro il tuo programma, non in una chat a parte.",
			cta2: "Scopri il prodotto",
			trust: [
				"Web app installabile su iPhone e Android",
				"Italiano e inglese",
				"Tema chiaro e scuro",
			],
		},
		problems: [
			{
				k: "Il programma",
				v: "Ore perse su Excel, copia-incolla, versioni che non tornano.",
			},
			{
				k: "Gli atleti",
				v: "Risultati sparsi tra chat, fogli e screenshot. Zero visione d'insieme.",
			},
			{
				k: "La crescita",
				v: "Più atleti, più caos. Il tuo tempo è il vero collo di bottiglia.",
			},
		],
		product: {
			kicker: "Il prodotto",
			title: "Tutto il lavoro del coach, in un posto solo.",
			sub: "Quattro aree che si parlano tra loro: programmazione, AI, atleta, controllo. Niente da incollare da un'app all'altra.",
		},
		blocks: [
			{
				id: "programmazione",
				n: "01",
				kicker: "Programmazione",
				title: "Un builder nato per programmare davvero.",
				text: "Settimane, giorni e sedute multiple. Percentuali sul massimale con i carichi calcolati in automatico per ogni atleta. Conditioning con strutture native, EMOM, AMRAP, For Time e HIIT, non un campo note. E la stagione a mesocicli in un'unica vista, con un altro programma a fianco come riferimento.",
				points: [
					"Percentuali e carichi dal massimale di ogni atleta",
					"EMOM, AMRAP, For Time e HIIT nativi",
					"Vista a mesocicli e matrice delle progressioni",
					"Libreria esercizi con video, copia tra programmi",
				],
			},
			{
				id: "ai",
				n: "02",
				kicker: "Assistente AI",
				title: "L'AI lavora dentro il tuo programma.",
				text: "Apri il programma e chiedi: l'assistente aggiunge settimane, modifica le progressioni e collega gli esercizi reali della tua libreria, mostrandoti ogni passaggio. Usi la tua chiave API Anthropic o OpenAI e scegli tu il modello. Preferisci lavorare da ChatGPT o Claude? Li colleghi a Kraveon: leggono atleti, risultati e calendario e scrivono i programmi nell'app, con i permessi che decidi tu e un punto di ripristino prima di ogni modifica.",
				points: [
					"Modifica il programma aperto, non produce solo bozze",
					"La tua chiave API, il modello lo scegli tu",
					"Connettore per ChatGPT e Claude",
					"Permessi granulari e ripristino delle modifiche",
				],
			},
			{
				id: "atleti",
				n: "03",
				kicker: "Esperienza atleta",
				title: "Per l'atleta è un'app, non un PDF.",
				text: "Installabile sul telefono. Ogni giorno vede la seduta, i video degli esercizi e i carichi già calcolati, e registra serie, RPE e note in pochi tocchi. Il profilo prestativo e le classifiche mostrano i progressi reali; il diario benessere raccoglie sonno, energia e parametri come FC a riposo e HRV; calendario gare e notifiche tengono tutto sotto controllo.",
				points: [
					"Diario con carichi, RPE e note",
					"Profilo prestativo e classifiche",
					"Diario benessere e readiness",
					"Calendario gare e notifiche push",
				],
			},
			{
				id: "controllo",
				n: "04",
				kicker: "Controllo del coach",
				title: "Vedi chi spinge e chi va seguito. E rispondi dal posto giusto.",
				text: "La console feedback raccoglie risultati, RPE e note di tutti gli atleti in un unico feed, con filtri per non letti e da attenzionare e viste per programma o per atleta. I messaggi restano legati all'allenamento. Il calendario gare mostra tutta la squadra, con export CSV e ICS. E la fatturazione gestisce prodotti, fatture e pagamenti, manuali o online con Stripe.",
				points: [
					"Console feedback con filtri e risposta inline",
					"Messaggi nel contesto della seduta",
					"Calendario gare di squadra con export",
					"Fatture e incassi online",
				],
			},
		],
		manifesto: {
			kicker: "Da professionisti, per professionisti",
			t1: "Non è nato a tavolino.",
			t2: "È lo strumento che ",
			accent: "usiamo ogni giorno",
			p1a: "Dietro Kraveon c'è uno staff che vive la palestra tutti i giorni: oltre ",
			p1b: "10 anni",
			p1c: " tra preparazione atletica, coaching e gestione di box, con carriere da atleti agonisti di livello nazionale alle spalle.",
			p2a: "Ogni funzione nasce da un problema vero incontrato sul campo, non da una checklist di marketing. L'abbiamo costruita per risolvere i ",
			p2b: "nostri",
			p2c: " problemi di lavoro e la usiamo ",
			p2d: "attivamente con i nostri clienti",
			p2e: " ogni giorno: per programmare più in fretta e alzare la qualità di ciò che consegniamo.",
			p3: "Se alleni sul serio, parli la nostra stessa lingua. Kraveon è pensato esattamente per quella.",
			creds: [
				"Preparatori atletici certificati",
				"Coach & titolari di box",
				"10+ anni sul campo",
				"Atleti agonisti nazionali",
			],
		},
		box: {
			kicker: "Box & Team",
			title: "Più coach, più gruppi, una sola piattaforma.",
			sub: "Per box e centri con uno staff: ognuno vede i suoi atleti, nessuno duplica il lavoro.",
			tiles: [
				{
					icon: Users,
					t: "Più coach, più team",
					d: "Assegnazioni flessibili: un atleta può avere più coach e stare in più gruppi.",
				},
				{
					icon: Trophy,
					t: "Classifiche di gruppo",
					d: "Benchmark e WOD con classifiche per gruppo, per tenere la community agganciata.",
				},
				{
					icon: Layers,
					t: "Un account, due ruoli",
					d: "Chi allena e si allena usa un solo account e cambia modalità dal menu.",
				},
				{
					icon: MessageSquare,
					t: "Ognuno vede i suoi",
					d: "Ogni coach vede atleti, risultati e feedback dei propri gruppi, senza rumore dagli altri team.",
				},
			],
		},
		faq: {
			title: "Domande frequenti",
			items: [
				{
					q: "Devo saper programmare per usarlo?",
					a: "No. Il builder ti guida nella struttura e l'assistente AI prepara bozze solide da rifinire. Se sei esperto, hai il controllo totale su ogni dettaglio.",
				},
				{
					q: "I miei atleti devono pagare?",
					a: "No. L'atleta entra su invito del coach e usa l'app gratuitamente.",
				},
				{
					q: "Funziona da telefono?",
					a: "Sì. È una web app installabile (PWA): l'atleta la aggiunge alla schermata home e logga gli allenamenti come con un'app nativa, con notifiche push.",
				},
				{
					q: "Posso usare ChatGPT o Claude con Kraveon?",
					a: "Sì. Colleghi il tuo ChatGPT o Claude a Kraveon come connettore: legge atleti, risultati, libreria e calendario gare e scrive i programmi direttamente nell'app. Scegli tu i permessi e ogni modifica ha un punto di ripristino.",
				},
				{
					q: "Chi paga l'AI?",
					a: "L'assistente in app usa la tua chiave API Anthropic o OpenAI, quindi paghi solo l'uso al tuo provider e scegli il modello. Il connettore per ChatGPT o Claude usa il tuo abbonamento. Kraveon non aggiunge costi AI nascosti.",
				},
				{
					q: "Alleno e mi alleno anch'io: servono due account?",
					a: "No. Un solo account può essere coach e atleta insieme: cambi modalità dal menu e tieni programmi, diario e profilo in un unico posto.",
				},
				{
					q: "Posso migrare i miei programmi esistenti?",
					a: "Puoi ricreare i template rapidamente nel builder e l'AI ti aiuta a trasformare appunti e fogli in programmi strutturati.",
				},
			],
		},
		final: {
			title: "Pronto a fare il salto?",
			sub: "Entra nell'app e costruisci il tuo primo programma.",
		},
		footer: { privacy: "Privacy", cookie: "Cookie" },
		mock: {
			builder: {
				title: "Forza · Blocco 2",
				meta: "Settimana 3 / 6",
				oneRm: "% su 1RM di ogni atleta",
				days: [
					{
						d: "Lun",
						items: [
							{ n: "Back Squat", p: "5×3 @ 82%", tag: "118 kg" },
							{ n: "Panca piana", p: "4×6 @ 75%", tag: "71 kg" },
							{ n: "Trazioni", p: "4×8" },
						],
					},
					{
						d: "Mar",
						items: [
							{ n: "EMOM 12'", p: "1 Power Clean + 2 Front Squat", tag: "EMOM", accent: true },
							{ n: "Core", p: "3 giri · 40\" on / 20\" off" },
						],
					},
					{
						d: "Mer",
						items: [
							{ n: "Stacco", p: "4×4 @ 80%", tag: "140 kg" },
							{ n: "AMRAP 10'", p: "10 Cal Row · 8 Burpee · 6 Thruster", tag: "AMRAP", accent: true },
						],
					},
					{
						d: "Gio",
						items: [
							{ n: "Corsa", p: "6×400 m · rec 90\"" },
							{ n: "Mobilità", p: "15'" },
						],
					},
				],
				floatAi: { t: "Assistente AI", d: "Settimana 4 aggiunta · 6 esercizi collegati alla libreria" },
				floatLog: { t: "Giulia R. ha completato Lunedì", d: "Back Squat 118 kg · RPE 8 · \"ultima serie pesante\"" },
			},
			matrix: {
				title: "Progressioni · Blocco 2",
				meta: "4 settimane",
				head: ["Esercizio", "S1", "S2", "S3", "S4"],
				rows: [
					{ n: "Back Squat", v: ["75%", "80%", "85%", "65%"] },
					{ n: "Panca piana", v: ["70%", "75%", "80%", "60%"] },
					{ n: "Stacco", v: ["72%", "77%", "82%", "62%"] },
				],
				deload: "S4 scarico",
			},
			ai: {
				title: "Assistente AI · Forza · Blocco 2",
				meta: "La tua chiave",
				user: "Aggiungi una settimana di scarico dopo la 4 e porta gli squat al 65%.",
				steps: [
					"Letto il programma (4 settimane)",
					"Aggiunta Settimana 5 · Scarico",
					"Back Squat 4×5 @ 65% collegato alla libreria",
					"Punto di ripristino creato",
				],
				reply: "Fatto. La settimana 5 è pronta e non ancora pubblicata agli atleti. Vuoi che riduca anche il volume di panca?",
				connect: "Oppure collega ChatGPT o Claude",
			},
			phone: {
				time: "07:42",
				day: "Mercoledì 15",
				program: "Forza · Settimana 3",
				readiness: ["Sonno 7h 20", "Energia 4/5"],
				exercise: "Back Squat",
				rx: "5×3 @ 82% · 118 kg",
				sets: [
					{ s: "S1", kg: "118", r: "3", done: true },
					{ s: "S2", kg: "118", r: "3", done: true },
					{ s: "S3", kg: "120", r: "3", done: false },
				],
				rpe: "RPE",
				note: "Ultima serie pesante ma pulita",
				next: "EMOM 12' · 1 Power Clean + 2 Front Squat",
				cta: "Completa la seduta",
				kg: "kg",
				reps: "rep",
			},
			feedback: {
				title: "Feedback",
				meta: "3 non letti",
				filters: ["Tutti", "Non letti", "Da attenzionare", "Per programma", "Per atleta"],
				rows: [
					{ i: "GR", n: "Giulia R.", e: "Back Squat · 5×3", r: "118 kg", rpe: "8", note: "Ultima serie pesante ma pulita", reply: "Rispondi" },
					{ i: "MT", n: "Marco T.", e: "AMRAP 10'", r: "7 round + 4", rpe: "9", note: "Thruster troppo pesanti, serie spezzate", flag: "Da attenzionare", reply: "Rispondi" },
					{ i: "SB", n: "Sara B.", e: "Stacco · 4×4", r: "140 kg", rpe: "7", note: "Bene, pronta a salire", reply: "Rispondi" },
				],
			},
		},
	},
	en: {
		nav: [
			{ href: "#prodotto", label: "Product" },
			{ href: "#ai", label: "AI" },
			{ href: "#atleti", label: "Athletes" },
			{ href: "#box", label: "Box & Team" },
			{ href: "#faq", label: "FAQ" },
		],
		login: "Log in",
		app: "Go to app",
		menu: "Menu",
		hero: {
			kicker: "Coaching platform for coaches, S&C trainers and boxes",
			h1a: "Less time managing.",
			h1b: "More time ",
			h1accent: "coaching",
			sub: "Programs, athlete diary, feedback, competitions and payments in one platform. With an AI assistant that works inside your program, not in a separate chat.",
			cta2: "See the product",
			trust: [
				"Installable web app on iPhone and Android",
				"Italian and English",
				"Light and dark theme",
			],
		},
		problems: [
			{
				k: "Programming",
				v: "Hours lost in spreadsheets, copy-paste and versions that never match.",
			},
			{
				k: "Athletes",
				v: "Results scattered across chats, sheets and screenshots. No overview.",
			},
			{
				k: "Growth",
				v: "More athletes, more chaos. Your time is the real bottleneck.",
			},
		],
		product: {
			kicker: "The product",
			title: "All of a coach's work, in one place.",
			sub: "Four areas that talk to each other: programming, AI, athlete, control. Nothing to paste from one app to another.",
		},
		blocks: [
			{
				id: "programmazione",
				n: "01",
				kicker: "Programming",
				title: "A builder made for real programming.",
				text: "Weeks, days and multiple sessions. Percentages of the athlete's max with loads calculated automatically. Conditioning with native structures, EMOM, AMRAP, For Time and HIIT, not a notes field. And the season by mesocycles in a single view, with another program alongside as a reference.",
				points: [
					"Percentages and loads from each athlete's max",
					"Native EMOM, AMRAP, For Time and HIIT",
					"Mesocycle overview and progression matrix",
					"Exercise library with videos, copy across programs",
				],
			},
			{
				id: "ai",
				n: "02",
				kicker: "AI assistant",
				title: "The AI works inside your program.",
				text: "Open the program and ask: the assistant adds weeks, edits progressions and links the real exercises from your library, showing you every step. You use your own Anthropic or OpenAI API key and pick the model. Prefer working from ChatGPT or Claude? Connect them to Kraveon: they read athletes, results and calendar and write programs into the app, with the permissions you decide and a restore point before every change.",
				points: [
					"Edits the open program, not just drafts",
					"Your API key, the model you choose",
					"Connector for ChatGPT and Claude",
					"Granular permissions and change restore",
				],
			},
			{
				id: "atleti",
				n: "03",
				kicker: "Athlete experience",
				title: "For the athlete it is an app, not a PDF.",
				text: "Installable on the phone. Every day they see the session, exercise videos and pre-calculated loads, and log sets, RPE and notes in a few taps. The performance profile and leaderboards show real progress; the wellness diary collects sleep, energy and metrics like resting HR and HRV; the competition calendar and notifications keep everything on track.",
				points: [
					"Diary with loads, RPE and notes",
					"Performance profile and leaderboards",
					"Wellness diary and readiness",
					"Competition calendar and push notifications",
				],
			},
			{
				id: "controllo",
				n: "04",
				kicker: "Coach control",
				title: "See who is pushing and who needs attention. Reply from the right place.",
				text: "The feedback console gathers results, RPE and notes from every athlete in one feed, with filters for unread and needs-attention and views by program or by athlete. Messages stay tied to the workout. The competition calendar shows the whole team, with CSV and ICS export. And billing handles products, invoices and payments, manual or online with Stripe.",
				points: [
					"Feedback console with filters and inline reply",
					"Messages in the session's context",
					"Team competition calendar with export",
					"Invoices and online payments",
				],
			},
		],
		manifesto: {
			kicker: "Built by professionals, for professionals",
			t1: "It was not built in a boardroom.",
			t2: "It is the tool we ",
			accent: "use every day",
			p1a: "Behind Kraveon there is a team that lives training on the floor every day: over ",
			p1b: "10 years",
			p1c: " in athletic preparation, coaching and box management, with national-level competitive athlete backgrounds.",
			p2a: "Every feature starts from a real field problem, not a marketing checklist. We built it to solve ",
			p2b: "our",
			p2c: " operational issues and we use it ",
			p2d: "actively with our clients",
			p2e: " every day to program faster and raise delivery quality.",
			p3: "If you coach seriously, you speak our language. Kraveon is built exactly for that.",
			creds: [
				"Certified strength coaches",
				"Coaches & box owners",
				"10+ years on the floor",
				"National-level competitive athletes",
			],
		},
		box: {
			kicker: "Box & Team",
			title: "More coaches, more groups, one platform.",
			sub: "For boxes and centers with a staff: everyone sees their athletes, nobody duplicates the work.",
			tiles: [
				{
					icon: Users,
					t: "More coaches, more teams",
					d: "Flexible assignments: an athlete can have several coaches and belong to several groups.",
				},
				{
					icon: Trophy,
					t: "Group leaderboards",
					d: "Benchmarks and WODs with per-group rankings to keep the community engaged.",
				},
				{
					icon: Layers,
					t: "One account, two roles",
					d: "Those who coach and train use a single account and switch mode from the menu.",
				},
				{
					icon: MessageSquare,
					t: "Everyone sees their own",
					d: "Each coach sees athletes, results and feedback of their own groups, with no noise from other teams.",
				},
			],
		},
		faq: {
			title: "Frequently asked questions",
			items: [
				{
					q: "Do I need programming skills to use it?",
					a: "No. The builder guides the structure and the AI assistant prepares solid drafts to refine. If you are advanced, you keep full control over every detail.",
				},
				{
					q: "Do my athletes need to pay?",
					a: "No. Athletes join through the coach's invite and use the app for free.",
				},
				{
					q: "Does it work on mobile?",
					a: "Yes. It is an installable web app (PWA): athletes add it to the home screen and log workouts like a native app, with push notifications.",
				},
				{
					q: "Can I use ChatGPT or Claude with Kraveon?",
					a: "Yes. Connect your ChatGPT or Claude to Kraveon as a connector: it reads athletes, results, library and competition calendar and writes programs directly into the app. You choose the permissions and every change has a restore point.",
				},
				{
					q: "Who pays for the AI?",
					a: "The in-app assistant uses your own Anthropic or OpenAI API key, so you only pay your provider for usage and pick the model. The ChatGPT or Claude connector uses your subscription. Kraveon adds no hidden AI costs.",
				},
				{
					q: "I coach and I train too: do I need two accounts?",
					a: "No. One account can be coach and athlete at the same time: switch mode from the menu and keep programs, diary and profile in one place.",
				},
				{
					q: "Can I migrate my existing programs?",
					a: "You can rebuild templates quickly in the builder, and the AI helps you turn notes and sheets into structured programs.",
				},
			],
		},
		final: {
			title: "Ready to level up?",
			sub: "Enter the app and build your first program.",
		},
		footer: { privacy: "Privacy", cookie: "Cookies" },
		mock: {
			builder: {
				title: "Strength · Block 2",
				meta: "Week 3 / 6",
				oneRm: "% of each athlete's 1RM",
				days: [
					{
						d: "Mon",
						items: [
							{ n: "Back Squat", p: "5×3 @ 82%", tag: "118 kg" },
							{ n: "Bench Press", p: "4×6 @ 75%", tag: "71 kg" },
							{ n: "Pull-ups", p: "4×8" },
						],
					},
					{
						d: "Tue",
						items: [
							{ n: "EMOM 12'", p: "1 Power Clean + 2 Front Squat", tag: "EMOM", accent: true },
							{ n: "Core", p: "3 rounds · 40\" on / 20\" off" },
						],
					},
					{
						d: "Wed",
						items: [
							{ n: "Deadlift", p: "4×4 @ 80%", tag: "140 kg" },
							{ n: "AMRAP 10'", p: "10 Cal Row · 8 Burpee · 6 Thruster", tag: "AMRAP", accent: true },
						],
					},
					{
						d: "Thu",
						items: [
							{ n: "Running", p: "6×400 m · rest 90\"" },
							{ n: "Mobility", p: "15'" },
						],
					},
				],
				floatAi: { t: "AI assistant", d: "Week 4 added · 6 exercises linked to the library" },
				floatLog: { t: "Giulia R. completed Monday", d: "Back Squat 118 kg · RPE 8 · \"last set was heavy\"" },
			},
			matrix: {
				title: "Progressions · Block 2",
				meta: "4 weeks",
				head: ["Exercise", "W1", "W2", "W3", "W4"],
				rows: [
					{ n: "Back Squat", v: ["75%", "80%", "85%", "65%"] },
					{ n: "Bench Press", v: ["70%", "75%", "80%", "60%"] },
					{ n: "Deadlift", v: ["72%", "77%", "82%", "62%"] },
				],
				deload: "W4 deload",
			},
			ai: {
				title: "AI assistant · Strength · Block 2",
				meta: "Your key",
				user: "Add a deload week after week 4 and bring squats down to 65%.",
				steps: [
					"Read the program (4 weeks)",
					"Added Week 5 · Deload",
					"Back Squat 4×5 @ 65% linked to the library",
					"Restore point created",
				],
				reply: "Done. Week 5 is ready and not yet published to athletes. Want me to reduce bench volume too?",
				connect: "Or connect ChatGPT or Claude",
			},
			phone: {
				time: "07:42",
				day: "Wednesday 15",
				program: "Strength · Week 3",
				readiness: ["Sleep 7h 20", "Energy 4/5"],
				exercise: "Back Squat",
				rx: "5×3 @ 82% · 118 kg",
				sets: [
					{ s: "S1", kg: "118", r: "3", done: true },
					{ s: "S2", kg: "118", r: "3", done: true },
					{ s: "S3", kg: "120", r: "3", done: false },
				],
				rpe: "RPE",
				note: "Last set heavy but clean",
				next: "EMOM 12' · 1 Power Clean + 2 Front Squat",
				cta: "Complete session",
				kg: "kg",
				reps: "reps",
			},
			feedback: {
				title: "Feedback",
				meta: "3 unread",
				filters: ["All", "Unread", "Needs attention", "By program", "By athlete"],
				rows: [
					{ i: "GR", n: "Giulia R.", e: "Back Squat · 5×3", r: "118 kg", rpe: "8", note: "Last set heavy but clean", reply: "Reply" },
					{ i: "MT", n: "Marco T.", e: "AMRAP 10'", r: "7 rounds + 4", rpe: "9", note: "Thrusters too heavy, broken sets", flag: "Needs attention", reply: "Reply" },
					{ i: "SB", n: "Sara B.", e: "Deadlift · 4×4", r: "140 kg", rpe: "7", note: "Good, ready to go up", reply: "Reply" },
				],
			},
		},
	},
};

const BLOCK_ICONS = {
	programmazione: Dumbbell,
	ai: Sparkles,
	atleti: Smartphone,
	controllo: MessageSquare,
};

const POINT_ICONS = {
	programmazione: [Percent, Timer, Layers, Dumbbell],
	ai: [Sparkles, Bot, Plug, RotateCcw],
	atleti: [Smartphone, Trophy, HeartPulse, Bell],
	controllo: [MessageSquare, MessageSquare, CalendarDays, Receipt],
};

export default function Landing() {
	const { user, loading } = useAuth();
	const { language } = useLanguage();
	const navigate = useNavigate();
	const [mobileOpen, setMobileOpen] = useState(false);
	const [activeNavHref, setActiveNavHref] = useState("");
	const [scrolled, setScrolled] = useState(false);
	const c = COPY[language === "en" ? "en" : "it"];

	// Durante il controllo sessione: se esiste un token l'utente è quasi
	// certamente loggato → non mostrare la landing (niente "flash" del
	// marketing) e attendi il redirect a /dashboard.
	const hasSession =
		typeof window !== "undefined" &&
		!!window.localStorage.getItem("session_token");
	const showLanding = !(loading && hasSession);

	useLandingDocumentScroll();
	useMetricoolTracker();

	// Header: trasparente in cima, vetro appena si scorre.
	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 24);
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	// Scroll-spy sulle sezioni. Riparte quando la landing passa da
	// "nascosta" (controllo sessione) a visibile.
	useEffect(() => {
		if (!showLanding || typeof IntersectionObserver === "undefined") return;

		const sections = c.nav
			.map((l) => document.querySelector(l.href))
			.filter(Boolean);
		const spy = new IntersectionObserver(
			(entries) => {
				entries.forEach((e) => {
					if (e.isIntersecting) setActiveNavHref(`#${e.target.id}`);
				});
			},
			{ rootMargin: "-40% 0px -55% 0px", threshold: 0 },
		);
		sections.forEach((s) => spy.observe(s));
		return () => spy.disconnect();
	}, [showLanding, c.nav]);

	useRevealOnScroll(showLanding, language);

	if (!showLanding) {
		return null;
	}

	// Utente già autenticato → vai all'app
	if (!loading && user) {
		return <Navigate to="/dashboard" replace />;
	}

	const goLogin = () => navigate("/login");
	const scrollTo = (id) =>
		document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

	return (
		<div className="theme-dark-scope min-h-screen bg-obsidian text-white font-body antialiased selection:bg-volt/30">
			{/* NAV */}
			<header
				className={`fixed top-0 inset-x-0 z-50 border-b transition-colors duration-300 ${
					scrolled || mobileOpen
						? "bg-black/70 backdrop-blur-xl border-white/10"
						: "bg-transparent border-transparent"
				}`}
			>
				<div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-6">
					<a href="#top" className="flex items-center gap-2.5 shrink-0">
						<img src="/logo-kraveon.svg" alt="Kraveon" className="h-8 w-8" />
						<span className="font-heading font-extrabold uppercase tracking-tight text-xl">
							Kraveon
						</span>
					</a>

					<nav className="hidden md:flex items-center gap-1 text-sm">
						{c.nav.map((l) => {
							const active = activeNavHref === l.href;
							return (
								<a
									key={l.href}
									href={l.href}
									aria-current={active ? "true" : undefined}
									className={`relative px-3 py-2 rounded-sm transition-colors ${
										active
											? "text-white"
											: "text-white/60 hover:text-white"
									}`}
								>
									{l.label}
									<span
										className={`absolute left-3 right-3 -bottom-px h-px bg-volt transition-opacity ${
											active ? "opacity-100" : "opacity-0"
										}`}
									/>
								</a>
							);
						})}
					</nav>

					<div className="hidden md:flex items-center gap-3">
						<LanguageToggle />
						<Button
							variant="ghost"
							onClick={goLogin}
							className="text-white hover:bg-white/5 rounded-sm"
						>
							{c.login}
						</Button>
						<Button
							onClick={goLogin}
							className="bg-volt text-black font-bold uppercase tracking-wider rounded-sm hover:bg-volt/90"
						>
							{c.app}
						</Button>
					</div>

					<button
						className="md:hidden text-white p-2 -mr-2"
						onClick={() => setMobileOpen((v) => !v)}
						aria-label={c.menu}
						aria-expanded={mobileOpen}
					>
						{mobileOpen ? <X size={22} /> : <Menu size={22} />}
					</button>
				</div>

				{mobileOpen && (
					<div className="md:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl px-5 py-4 space-y-1">
						{c.nav.map((l) => (
							<a
								key={l.href}
								href={l.href}
								onClick={() => setMobileOpen(false)}
								className="block text-white/75 hover:text-white py-2.5 text-base border-b border-white/5 last:border-b-0"
							>
								{l.label}
							</a>
						))}
						<div className="pt-4 space-y-3">
							<div className="grid grid-cols-2 gap-2">
								<Button
									variant="outline"
									onClick={goLogin}
									className="w-full rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5"
								>
									{c.login}
								</Button>
								<Button
									onClick={goLogin}
									className="w-full bg-volt text-black font-bold uppercase tracking-wider rounded-sm hover:bg-volt/90"
								>
									{c.app}
								</Button>
							</div>
							<div className="flex justify-center">
								<LanguageToggle />
							</div>
						</div>
					</div>
				)}
			</header>

			{/* HERO */}
			<section
				id="top"
				className="relative pt-28 pb-20 md:pt-40 md:pb-28 overflow-hidden"
			>
				<div className="absolute inset-0 krv-grid-bg pointer-events-none" />
				<div
					className="absolute inset-0 pointer-events-none"
					style={{
						background:
							"radial-gradient(ellipse 70% 50% at 50% -10%, rgba(0,214,143,0.18) 0%, rgba(0,0,0,0) 70%)",
					}}
				/>
				<div className="relative max-w-6xl mx-auto px-5">
					<div className="max-w-4xl mx-auto text-center">
						<span className="krv-kicker krv-kicker--wrap inline-flex items-start sm:items-center gap-2.5 text-left sm:text-center max-w-[20rem] sm:max-w-none">
							<span className="mt-[0.3em] sm:mt-0 h-1.5 w-1.5 shrink-0 rounded-full bg-volt shadow-[0_0_0_3px_rgba(0,214,143,0.2)]" />
							{c.hero.kicker}
						</span>
						<h1 className="mt-6 font-heading font-extrabold uppercase tracking-tight text-[2.75rem] leading-[0.95] sm:text-6xl md:text-7xl lg:text-[5rem]">
							{c.hero.h1a}
							<br />
							{c.hero.h1b}
							<span className="text-volt">{c.hero.h1accent}</span>.
						</h1>
						<p className="mt-6 text-lg md:text-xl text-white/70 leading-relaxed max-w-2xl mx-auto">
							{c.hero.sub}
						</p>
						<div className="mt-8 flex flex-col sm:flex-row sm:justify-center gap-3">
							<Button
								onClick={goLogin}
								className="h-12 px-7 bg-volt text-black font-bold uppercase tracking-wider rounded-sm hover:bg-volt/90 text-base"
							>
								{c.app} <ArrowRight size={18} />
							</Button>
							<Button
								variant="outline"
								onClick={() => scrollTo("prodotto")}
								className="h-12 px-7 rounded-sm border-white/15 bg-transparent text-white hover:bg-white/5 text-base"
							>
								{c.hero.cta2} <ChevronDown size={18} />
							</Button>
						</div>
						<ul className="mt-7 mx-auto w-fit flex flex-col items-start gap-y-2 text-sm text-white/55 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-center sm:gap-x-6">
							{c.hero.trust.map((item) => (
								<li key={item} className="inline-flex items-start gap-2 text-left">
									<Check
										size={14}
										className="text-volt shrink-0 mt-[0.2em]"
										strokeWidth={2.5}
									/>
									{item}
								</li>
							))}
						</ul>
					</div>

					<div className="relative mt-14 md:mt-20" data-reveal>
						<div
							className="absolute -inset-x-10 -top-10 h-40 pointer-events-none"
							style={{
								background:
									"radial-gradient(ellipse 60% 100% at 50% 100%, rgba(0,214,143,0.14) 0%, rgba(0,0,0,0) 70%)",
							}}
						/>
						<BuilderMock m={c.mock.builder} />
					</div>
				</div>
			</section>

			{/* PROBLEMA */}
			<section className="border-y border-white/10 bg-charcoal/50">
				<div className="max-w-6xl mx-auto px-5 py-14 md:py-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
					{c.problems.map((p, i) => (
						<div
							key={p.k}
							className={`md:px-6 ${i > 0 ? "md:border-l md:border-white/10" : ""}`}
							data-reveal
							style={{ transitionDelay: `${i * 90}ms` }}
						>
							<div className="font-mono text-xs uppercase tracking-[0.16em] text-blaze">
								{p.k}
							</div>
							<p className="mt-3 font-heading font-semibold uppercase tracking-tight text-2xl leading-tight text-white/90">
								{p.v}
							</p>
						</div>
					))}
				</div>
			</section>

			{/* PRODOTTO */}
			<section id="prodotto" className="scroll-mt-20">
				<div className="max-w-6xl mx-auto px-5 pt-20 md:pt-28">
					<SectionHeader
						label={c.product.kicker}
						title={c.product.title}
						subtitle={c.product.sub}
					/>
				</div>

				<div className="max-w-6xl mx-auto px-5 mt-6 md:mt-10">
					{c.blocks.map((b, i) => {
						const Icon = BLOCK_ICONS[b.id];
						const reversed = i % 2 === 1;
						let visual;
						if (b.id === "programmazione")
							visual = <MatrixMock m={c.mock.matrix} />;
						else if (b.id === "ai") visual = <AiMock m={c.mock.ai} />;
						else if (b.id === "atleti") visual = <PhoneMock m={c.mock.phone} />;
						else visual = <FeedbackMock m={c.mock.feedback} />;

						return (
							<article
								key={b.id}
								id={b.id === "programmazione" || b.id === "controllo" ? undefined : b.id}
								className="scroll-mt-24 py-14 md:py-20 border-t border-white/10 first:border-t-0 grid grid-cols-1 gap-10 lg:gap-16 lg:grid-cols-2 lg:items-center"
							>
								<div
									className={`min-w-0 ${reversed ? "lg:order-2" : ""}`}
									data-reveal
								>
									<div className="flex items-center gap-3">
										<span className="krv-icon-frame krv-icon-frame--sm">
											<Icon className="h-4 w-4" strokeWidth={2} />
										</span>
										<span className="krv-kicker">
											{b.n} · {b.kicker}
										</span>
									</div>
									<h3 className="mt-5 font-heading font-extrabold uppercase tracking-tight text-3xl md:text-[2.6rem] leading-[1.02]">
										{b.title}
									</h3>
									<p className="mt-5 text-base md:text-lg text-white/70 leading-relaxed">
										{b.text}
									</p>
									<ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
										{b.points.map((pt, j) => {
											const PIcon = POINT_ICONS[b.id][j] || Check;
											return (
												<li
													key={pt}
													className="flex items-start gap-3 rounded-sm border border-white/10 bg-white/[0.025] px-3.5 py-3 text-sm text-white/85 leading-snug"
												>
													<PIcon
														className="h-4 w-4 text-volt shrink-0 mt-0.5"
														strokeWidth={2}
													/>
													<span>{pt}</span>
												</li>
											);
										})}
									</ul>
								</div>
								<div
									className={`min-w-0 ${reversed ? "lg:order-1" : ""} ${
										b.id === "atleti" ? "flex justify-center" : ""
									}`}
									data-reveal
									style={{ transitionDelay: "120ms" }}
								>
									{visual}
								</div>
							</article>
						);
					})}
				</div>
			</section>

			{/* MANIFESTO — sviluppato da professionisti, per professionisti */}
			<section id="perche" className="relative overflow-hidden border-y border-white/10 bg-charcoal/50">
				<div
					className="absolute inset-0 pointer-events-none"
					style={{
						background:
							"radial-gradient(circle at 85% 20%, rgba(0,214,143,0.10) 0%, rgba(0,0,0,0) 55%)",
					}}
				/>
				<div className="relative max-w-6xl mx-auto px-5 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-start">
					<div data-reveal>
						<span className="krv-kicker inline-flex items-center gap-2.5">
							<span className="h-1.5 w-1.5 rounded-full bg-volt" />
							{c.manifesto.kicker}
						</span>
						<h2 className="mt-5 font-heading font-extrabold uppercase tracking-tight text-3xl md:text-5xl leading-[1.02]">
							{c.manifesto.t1}
							<br />
							{c.manifesto.t2}
							<span className="text-volt">{c.manifesto.accent}</span>.
						</h2>
						<div className="mt-6 space-y-4 text-white/70 text-base md:text-lg leading-relaxed">
							<p>
								{c.manifesto.p1a}
								<span className="text-white font-semibold">
									{c.manifesto.p1b}
								</span>
								{c.manifesto.p1c}
							</p>
							<p>
								{c.manifesto.p2a}
								<span className="text-white">{c.manifesto.p2b}</span>
								{c.manifesto.p2c}
								<span className="text-white">{c.manifesto.p2d}</span>
								{c.manifesto.p2e}
							</p>
							<p>{c.manifesto.p3}</p>
						</div>
					</div>

					<div
						className="lg:mt-16 border border-white/10 bg-black/40 rounded-sm divide-y divide-white/10"
						data-reveal
						style={{ transitionDelay: "120ms" }}
					>
						{c.manifesto.creds.map((cred, idx) => (
							<div key={cred} className="flex items-center gap-4 px-5 py-4">
								<span className="font-mono text-xs text-white/35 w-6 shrink-0">
									{String(idx + 1).padStart(2, "0")}
								</span>
								<span className="krv-icon-frame krv-icon-frame--sm shrink-0">
									<Check size={16} strokeWidth={2.5} />
								</span>
								<span className="font-heading font-bold uppercase tracking-tight text-base sm:text-lg leading-tight text-white/90">
									{cred}
								</span>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* PER I BOX */}
			<section id="box" className="scroll-mt-20 max-w-6xl mx-auto px-5 py-20 md:py-28">
				<div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16 items-start">
					<div data-reveal>
						<SectionHeader
							label={c.box.kicker}
							title={c.box.title}
							subtitle={c.box.sub}
							align="left"
						/>
						<Button
							onClick={goLogin}
							className="mt-8 h-11 px-6 bg-volt text-black font-bold uppercase tracking-wider rounded-sm hover:bg-volt/90"
						>
							{c.app} <ChevronRight size={18} />
						</Button>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-reveal style={{ transitionDelay: "120ms" }}>
						{c.box.tiles.map((tile) => (
							<div
								key={tile.t}
								className="group relative overflow-hidden rounded-sm border border-white/10 bg-card p-6 transition-colors hover:border-volt/40"
							>
								<div
									className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
									style={{
										background:
											"radial-gradient(circle at 0% 0%, rgba(0,214,143,0.10) 0%, rgba(0,0,0,0) 60%)",
									}}
								/>
								<span className="krv-icon-frame krv-icon-frame--md">
									<tile.icon className="h-5 w-5" strokeWidth={1.75} />
								</span>
								<h3 className="mt-5 font-heading font-bold uppercase tracking-tight text-xl leading-tight">
									{tile.t}
								</h3>
								<p className="mt-2 text-sm text-white/65 leading-relaxed">
									{tile.d}
								</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section id="faq" className="scroll-mt-20 border-t border-white/10 bg-charcoal/50">
				<div className="max-w-3xl mx-auto px-5 py-20 md:py-28">
					<SectionHeader label="FAQ" title={c.faq.title} />
					<div className="mt-10 divide-y divide-white/10 border-y border-white/10" data-reveal>
						{c.faq.items.map((f) => (
							<details key={f.q} className="group py-5">
								<summary className="flex items-center justify-between gap-6 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
									<span className="font-heading font-semibold uppercase tracking-tight text-lg md:text-xl leading-tight">
										{f.q}
									</span>
									<span className="krv-icon-frame krv-icon-frame--sm shrink-0 transition-transform group-open:rotate-45">
										<span className="relative block h-3 w-3">
											<span className="absolute left-0 right-0 top-1/2 -mt-px h-0.5 bg-current" />
											<span className="absolute top-0 bottom-0 left-1/2 -ml-px w-0.5 bg-current" />
										</span>
									</span>
								</summary>
								<p className="mt-3 pr-12 text-white/70 leading-relaxed">{f.a}</p>
							</details>
						))}
					</div>
				</div>
			</section>

			{/* CTA FINALE */}
			<section className="relative overflow-hidden border-t border-white/10">
				<div className="absolute inset-0 krv-grid-bg pointer-events-none" style={{ transform: "scaleY(-1)" }} />
				<div
					className="absolute inset-0 pointer-events-none"
					style={{
						background:
							"radial-gradient(ellipse 60% 60% at 50% 110%, rgba(0,214,143,0.22) 0%, rgba(0,0,0,0) 70%)",
					}}
				/>
				<div className="relative max-w-3xl mx-auto px-5 py-24 md:py-32 text-center" data-reveal>
					<h2 className="font-heading font-extrabold uppercase tracking-tight text-4xl md:text-6xl leading-[1.02]">
						{c.final.title}
					</h2>
					<p className="mt-4 text-lg text-white/70">{c.final.sub}</p>
					<Button
						onClick={goLogin}
						className="mt-8 h-12 px-8 bg-volt text-black font-bold uppercase tracking-wider rounded-sm hover:bg-volt/90 text-base"
					>
						{c.app} <ArrowRight size={18} />
					</Button>
				</div>
			</section>

			{/* FOOTER */}
			<footer className="border-t border-white/10">
				<div className="max-w-6xl mx-auto px-5 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
					<div className="flex items-center gap-2.5">
						<img src="/logo-kraveon.svg" alt="Kraveon" className="h-7 w-7" />
						<span className="font-heading font-extrabold uppercase tracking-tight">
							Kraveon
						</span>
					</div>
					<div className="flex items-center gap-6 text-sm text-white/60">
						<Link to="/privacy-policy" className="hover:text-white">
							{c.footer.privacy}
						</Link>
						<Link to="/cookie-policy" className="hover:text-white">
							{c.footer.cookie}
						</Link>
						<button onClick={goLogin} className="hover:text-white">
							{c.login}
						</button>
					</div>
					<p className="text-xs text-white/45 font-mono">
						© {new Date().getFullYear()} Kraveon
					</p>
				</div>
			</footer>
		</div>
	);
}

/* ---------- Anteprime prodotto (illustrative) ---------- */

function MockWindow({ title, meta, children, className = "" }) {
	return (
		<div className={`krv-mock ${className}`} aria-hidden="true">
			<div className="krv-mock-bar">
				<span className="krv-mock-dots">
					<i />
					<i />
					<i />
				</span>
				<span className="krv-mock-title">{title}</span>
				{meta && <span className="krv-mock-meta">{meta}</span>}
			</div>
			<div className="krv-mock-body">{children}</div>
		</div>
	);
}

function BuilderMock({ m }) {
	return (
		<div className="relative">
			<MockWindow title={`${m.title} · ${m.meta}`}>
				<div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
					{m.days.map((day) => (
						<div
							key={day.d}
							className="rounded-sm border border-white/[0.08] bg-white/[0.02] p-2.5 min-h-[9.5rem]"
						>
							<div className="flex items-center justify-between">
								<span className="font-heading font-bold uppercase tracking-tight text-sm text-white/85">
									{day.d}
								</span>
								<span className="h-1.5 w-1.5 rounded-full bg-volt/70" />
							</div>
							<div className="mt-2 space-y-1.5">
								{day.items.map((it) => (
									<div
										key={it.n + it.p}
										className={`rounded-sm border px-2 py-1.5 ${
											it.accent
												? "border-volt/30 bg-volt/[0.07]"
												: "border-white/[0.08] bg-black/40"
										}`}
									>
										<div className="flex items-center justify-between gap-2">
											<span className="text-[12px] font-semibold text-white/90 leading-snug min-w-0 sm:truncate">
												{it.n}
											</span>
											{it.tag && (
												<span
													className={`krv-tag shrink-0 ${
														it.accent
															? "bg-volt/20 text-volt"
															: "bg-white/[0.06] text-white/70"
													}`}
												>
													{it.tag}
												</span>
											)}
										</div>
										<div className="text-[11px] text-white/55 leading-snug mt-0.5">
											{it.p}
										</div>
									</div>
								))}
							</div>
						</div>
					))}
				</div>
				<div className="mt-3 flex items-center justify-end gap-2 text-[11px] text-white/45 font-mono">
					<Percent className="h-3 w-3 text-volt/80" />
					{m.oneRm}
				</div>
			</MockWindow>

			<div className="krv-float hidden sm:flex items-start gap-3 -top-5 right-6 md:right-10 max-w-[280px] px-3.5 py-3">
				<span className="krv-icon-frame krv-icon-frame--sm shrink-0">
					<Sparkles className="h-4 w-4" strokeWidth={2} />
				</span>
				<div className="min-w-0">
					<div className="text-xs font-semibold text-white/90">{m.floatAi.t}</div>
					<div className="text-[11px] text-white/60 leading-snug mt-0.5">
						{m.floatAi.d}
					</div>
				</div>
			</div>

			<div className="krv-float hidden sm:flex items-start gap-3 -bottom-6 left-6 md:left-10 max-w-[320px] px-3.5 py-3">
				<span className="h-9 w-9 shrink-0 rounded-sm bg-volt/15 border border-volt/30 text-volt font-heading font-bold text-sm flex items-center justify-center">
					GR
				</span>
				<div className="min-w-0">
					<div className="text-xs font-semibold text-white/90">{m.floatLog.t}</div>
					<div className="text-[11px] text-white/60 leading-snug mt-0.5">
						{m.floatLog.d}
					</div>
				</div>
			</div>
		</div>
	);
}

function MatrixMock({ m }) {
	return (
		<MockWindow title={m.title} meta={m.meta}>
			<div className="overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className="text-left">
							{m.head.map((h, i) => (
								<th
									key={h}
									className={`pb-2 font-mono text-[11px] uppercase tracking-[0.12em] font-medium ${
										i === 0 ? "text-white/45" : "text-white/60 text-center"
									} ${i === m.head.length - 1 ? "text-amber-300/80" : ""}`}
								>
									{h}
								</th>
							))}
						</tr>
					</thead>
					<tbody className="divide-y divide-white/[0.08]">
						{m.rows.map((row) => (
							<tr key={row.n}>
								<td className="py-3 pr-2 sm:pr-3 text-[13px] sm:text-sm font-semibold text-white/90 whitespace-nowrap">
									{row.n}
								</td>
								{row.v.map((v, i) => {
									const last = i === row.v.length - 1;
									const prev = i > 0 ? parseInt(row.v[i - 1], 10) : null;
									const cur = parseInt(v, 10);
									const delta = prev === null ? null : cur - prev;
									return (
										<td key={i} className="py-3 text-center">
											<div
												className={`inline-flex flex-col items-center rounded-sm border px-1.5 py-1.5 min-w-[2.8rem] sm:px-2.5 sm:min-w-[3.6rem] ${
													last
														? "border-amber-300/25 bg-amber-300/[0.06]"
														: "border-white/10 bg-white/[0.03]"
												}`}
											>
												<span className="font-heading font-bold text-[15px] sm:text-base leading-none">
													{v}
												</span>
												{delta !== null && (
													<span
														className={`mt-1 font-mono text-[10px] leading-none ${
															delta > 0 ? "text-volt" : "text-amber-300/80"
														}`}
													>
														{delta > 0 ? `+${delta}` : delta}
													</span>
												)}
											</div>
										</td>
									);
								})}
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] font-mono text-white/45">
				<span className="inline-flex items-center gap-1.5">
					<span className="h-2 w-2 rounded-sm bg-volt/70" /> +%
				</span>
				<span className="inline-flex items-center gap-1.5">
					<span className="h-2 w-2 rounded-sm bg-amber-300/70" /> {m.deload}
				</span>
			</div>
		</MockWindow>
	);
}

function AiMock({ m }) {
	return (
		<MockWindow title={m.title} meta={m.meta}>
			<div className="space-y-3">
				<div className="flex justify-end">
					<div className="max-w-[85%] rounded-sm bg-white/[0.07] border border-white/10 px-3.5 py-2.5 text-sm text-white/90 leading-snug">
						{m.user}
					</div>
				</div>
				<div className="flex gap-3">
					<span className="krv-icon-frame krv-icon-frame--sm shrink-0">
						<Bot className="h-4 w-4" strokeWidth={2} />
					</span>
					<div className="min-w-0 flex-1 space-y-2">
						<ul className="rounded-sm border border-white/10 bg-black/40 divide-y divide-white/[0.08]">
							{m.steps.map((s, i) => (
								<li
									key={s}
									className="flex items-center gap-2.5 px-3 py-2 text-[12.5px] text-white/80"
								>
									<span
										className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm ${
											i === m.steps.length - 1
												? "bg-amber-300/15 text-amber-300"
												: "bg-volt/15 text-volt"
										}`}
									>
										{i === m.steps.length - 1 ? (
											<RotateCcw className="h-2.5 w-2.5" strokeWidth={2.5} />
										) : (
											<Check className="h-2.5 w-2.5" strokeWidth={3} />
										)}
									</span>
									<span className="font-mono tracking-tight leading-snug min-w-0 sm:truncate">
										{s}
									</span>
								</li>
							))}
						</ul>
						<p className="text-sm text-white/85 leading-snug px-0.5">{m.reply}</p>
					</div>
				</div>
			</div>
			<div className="mt-4 pt-3 border-t border-white/[0.08] flex flex-wrap items-center gap-x-2 gap-y-2 text-[11px] font-mono text-white/50">
				<Plug className="h-3.5 w-3.5 shrink-0 text-volt/80" />
				<span className="min-w-0">{m.connect}</span>
				<span className="ml-auto inline-flex gap-1">
					<span className="krv-tag bg-white/[0.06] text-white/70">ChatGPT</span>
					<span className="krv-tag bg-white/[0.06] text-white/70">Claude</span>
				</span>
			</div>
		</MockWindow>
	);
}

function PhoneMock({ m }) {
	return (
		<div className="krv-phone w-full max-w-[300px]" aria-hidden="true">
			<div className="krv-phone-screen">
				<div className="flex items-center justify-between px-5 pt-3 text-[11px] font-semibold text-white/80">
					<span>{m.time}</span>
					<span className="h-4 w-16 rounded-full bg-black border border-white/10" />
					<span className="inline-flex gap-0.5 items-end">
						<i className="block w-0.5 h-1.5 bg-white/70" />
						<i className="block w-0.5 h-2 bg-white/70" />
						<i className="block w-0.5 h-2.5 bg-white/70" />
					</span>
				</div>
				<div className="px-4 pt-4 pb-3">
					<div className="font-mono text-[10px] uppercase tracking-[0.14em] text-volt">
						{m.program}
					</div>
					<div className="mt-1 font-heading font-extrabold uppercase tracking-tight text-2xl leading-none">
						{m.day}
					</div>
					<div className="mt-3 flex gap-1.5">
						{m.readiness.map((r) => (
							<span key={r} className="krv-pill text-[11px] py-1">
								<HeartPulse className="h-3 w-3 text-volt" />
								{r}
							</span>
						))}
					</div>
				</div>
				<div className="mx-3 rounded-sm border border-volt/30 bg-volt/[0.06] p-3">
					<div className="flex items-start justify-between gap-2">
						<div>
							<div className="font-semibold text-sm text-white/95">{m.exercise}</div>
							<div className="text-[11px] text-white/60 mt-0.5">{m.rx}</div>
						</div>
						<span className="krv-icon-frame krv-icon-frame--sm">
							<Dumbbell className="h-3.5 w-3.5" strokeWidth={2} />
						</span>
					</div>
					<div className="mt-3 space-y-1.5">
						{m.sets.map((s) => (
							<div
								key={s.s}
								className={`grid grid-cols-[2rem_1fr_1fr_1.5rem] items-center gap-2 rounded-sm border px-2 py-1.5 text-[12px] ${
									s.done
										? "border-white/[0.08] bg-black/40 text-white/70"
										: "border-volt/40 bg-black/60 text-white"
								}`}
							>
								<span className="font-mono text-[10px] text-white/50">{s.s}</span>
								<span className="rounded-sm bg-white/[0.05] px-1.5 py-0.5 text-center">
									{s.kg} <span className="text-white/40">{m.kg}</span>
								</span>
								<span className="rounded-sm bg-white/[0.05] px-1.5 py-0.5 text-center">
									{s.r} <span className="text-white/40">{m.reps}</span>
								</span>
								<span
									className={`flex h-4 w-4 items-center justify-center rounded-sm ${
										s.done ? "bg-volt text-black" : "border border-white/20"
									}`}
								>
									{s.done && <Check className="h-3 w-3" strokeWidth={3} />}
								</span>
							</div>
						))}
					</div>
					<div className="mt-3 flex items-center gap-2">
						<span className="font-mono text-[10px] uppercase tracking-[0.12em] text-white/50">
							{m.rpe}
						</span>
						<div className="flex gap-1">
							{["6", "7", "8", "9", "10"].map((n) => (
								<span
									key={n}
									className={`h-6 w-6 rounded-sm text-[11px] font-semibold flex items-center justify-center ${
										n === "8"
											? "bg-volt text-black"
											: "bg-white/[0.05] text-white/60"
									}`}
								>
									{n}
								</span>
							))}
						</div>
					</div>
					<div className="mt-2.5 rounded-sm bg-black/50 border border-white/[0.08] px-2 py-1.5 text-[11px] text-white/60 italic">
						"{m.note}"
					</div>
				</div>
				<div className="mx-3 mt-2 rounded-sm border border-white/[0.08] bg-white/[0.02] px-3 py-2.5 flex items-center justify-between">
					<div className="min-w-0">
						<div className="text-[12px] font-semibold text-white/85 truncate">{m.next}</div>
					</div>
					<span className="krv-tag bg-volt/20 text-volt shrink-0 ml-2">EMOM</span>
				</div>
				<div className="px-3 pt-3 pb-4">
					<div className="h-10 rounded-sm bg-volt text-black font-bold uppercase tracking-wider text-[12px] flex items-center justify-center">
						{m.cta}
					</div>
				</div>
			</div>
		</div>
	);
}

function FeedbackMock({ m }) {
	return (
		<MockWindow title={m.title} meta={m.meta}>
			<div className="flex flex-wrap gap-1.5">
				{m.filters.map((f, i) => (
					<span
						key={f}
						className={`krv-tag normal-case tracking-normal text-[11px] px-2 py-1 border ${
							i === 1
								? "border-volt/40 bg-volt/15 text-volt"
								: "border-white/10 bg-white/[0.03] text-white/65"
						}`}
					>
						{f}
					</span>
				))}
			</div>
			<ul className="mt-3 divide-y divide-white/[0.08] border-y border-white/[0.08]">
				{m.rows.map((r) => (
					<li key={r.n} className="py-3 flex gap-3">
						<span
							className={`h-9 w-9 shrink-0 rounded-sm font-heading font-bold text-sm flex items-center justify-center ${
								r.flag
									? "bg-blaze/15 border border-blaze/30 text-blaze"
									: "bg-volt/15 border border-volt/30 text-volt"
							}`}
						>
							{r.i}
						</span>
						<div className="min-w-0 flex-1">
							<div className="flex items-center gap-2 flex-wrap">
								<span className="text-sm font-semibold text-white/95">{r.n}</span>
								<span className="text-[12px] text-white/55">{r.e}</span>
								{r.flag && (
									<span className="krv-tag bg-blaze/15 text-blaze">{r.flag}</span>
								)}
							</div>
							<div className="mt-1 flex items-center gap-2 text-[12.5px]">
								<span className="font-heading font-bold text-base leading-none text-white">
									{r.r}
								</span>
								<span
									className={`krv-tag ${
										Number(r.rpe) >= 9
											? "bg-blaze/15 text-blaze"
											: "bg-white/[0.06] text-white/70"
									}`}
								>
									RPE {r.rpe}
								</span>
							</div>
							<div className="mt-1 text-[12px] text-white/55 italic leading-snug sm:truncate">
								"{r.note}"
							</div>
						</div>
						<span className="self-center text-[11px] font-semibold text-volt shrink-0 hidden sm:inline-flex items-center gap-1">
							{r.reply} <ChevronRight className="h-3 w-3" />
						</span>
					</li>
				))}
			</ul>
		</MockWindow>
	);
}
